import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF, gridNumberComparator } from '@mui/x-data-grid-pro';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TsrctSourcingCaseQuoteItem, TsrctSourcingCaseQuoteItemStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import {
  cellFactory,
  CurrencyCell,
  DataGridConfig,
  EmptyFallbackCell,
  GridAlignmentType,
  gridColDefFactory,
  SourcingCaseQuoteStatusCell,
} from '~/components/DataGridCustom';
import { CommodityLinkCell } from '~/components/DataGridCustom/cells/CommodityLinkCell/CommodityLinkCell';
import { CommodityLinkWithDescriptionCell } from '~/components/DataGridCustom/cells/CommodityLinkWithDescriptionCell/CommodityLinkWithDescriptionCell';
import { CuriosityValueCell } from '~/components/DataGridCustom/cells/CuriosityValueCell/CuriosityValueCell';
import { QuantityCell } from '~/components/DataGridCustom/cells/QuantityCell/QuantityCell';
import { RouteId } from '~/routes';

export enum QuoteLineItemGridField {
  ID = 'id',
  COMMODITY = 'commodity',
  TITLE = 'title',
  DESCRIPTION = 'description',
  UNIT_ID = 'unitId',
  DELIVERY_LOCATION_ID = 'deliveryLocationId',
  ORGANIZATION_ID = 'organizationId',
  COMMODITY_ID = 'commodityId',
  QUANTITY = 'quantity',
  PRICE_UNIT = 'priceUnit',
  SEQUENCE = 'sequence',
  STATUS = 'status',
  PRICE_PER_UNIT = 'pricePerUnit',
  PRICE_PER_PRICE_UNIT = 'pricePerPriceUnit',
  DISCOUNTED_PRICE_PER_UNIT = 'discountedPricePerUnit',
  MINIMUM_ORDER_QUANTITY = 'minimumOrderQuantity',
  TOTAL_VALUE = 'totalValue',
  TOTAL_DISCOUNTED_VALUE = 'totalDiscountedValue',
  CREATED_AT = 'createdAt',
}

export const useSourcingCaseQuoteDataGridConfig = (
  viewOnly: boolean = true,
): DataGridConfig<TsrctSourcingCaseQuoteItem> => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(I18nNamespace.common, { keyPrefix: 'SourcingCaseQuoteDataGrid.column' });
  return useMemo<DataGridConfig<TsrctSourcingCaseQuoteItem>>(() => {
    const createColumn = gridColDefFactory({ tColumn, t, defaultOptions: { sortable: false } });

    return {
      columnVisibilityModel: {
        [QuoteLineItemGridField.TITLE]: false,
        [QuoteLineItemGridField.COMMODITY_ID]: false,
        [QuoteLineItemGridField.PRICE_PER_UNIT]: false,
        [QuoteLineItemGridField.UNIT_ID]: false,
      },
      columns: [
        GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        createColumn({
          field: QuoteLineItemGridField.COMMODITY_ID,
          minWidth: 200,
          renderCell: cellFactory(CommodityLinkCell, ({ row }) => ({
            to: RouteId.commodityDetail(row.commodityId),
            ns: CuriosityNamespaceType.COMMODITY,
            needs: {
              permission: Permission.BaseCommodityList,
            },
          })),
        }),
        createColumn({
          field: QuoteLineItemGridField.COMMODITY,
          minWidth: 250,
          display: 'text',
          valueGetter: (_value, row) => {
            return row.commodityId;
          },
          renderCell: cellFactory(CommodityLinkWithDescriptionCell, ({ row }) => ({
            to: RouteId.commodityDetail(row.commodityId),
            needs: {
              permission: Permission.BaseCommodityList,
            },
            description: row.title,
          })),
        }),
        createColumn({
          field: QuoteLineItemGridField.ORGANIZATION_ID,
          minWidth: 150,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.ORGANIZATION,
          }),
        }),
        createColumn({
          field: QuoteLineItemGridField.QUANTITY,
          minWidth: 150,
          sortComparator: gridNumberComparator,
          renderCell: cellFactory(QuantityCell, ({ row }) => ({ value: row.quantity, unitId: row.unitId })),
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: QuoteLineItemGridField.UNIT_ID,
          minWidth: 150,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(CuriosityValueCell, {
            ns: CuriosityNamespaceType.UNIT,
          }),
        }),
        createColumn({
          field: QuoteLineItemGridField.PRICE_PER_UNIT,
          minWidth: 150,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({
            value: row.pricePerUnit,
            valueStrikeThrough: row.discountedPricePerUnit,
          })),
        }),
        createColumn({
          field: QuoteLineItemGridField.PRICE_PER_PRICE_UNIT,
          minWidth: 150,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({
            value: row.discountedPricePerPriceUnit,
            valueStrikeThrough: row.pricePerPriceUnit,
          })),
        }),
        createColumn({
          field: QuoteLineItemGridField.PRICE_UNIT,
          minWidth: 120,
          align: GridAlignmentType.RIGHT,
          renderCell: cellFactory(QuantityCell, ({ row }) => ({ value: row.priceUnit, unitId: row.unitId })),
          sortComparator: gridNumberComparator,
        }),
        createColumn({
          field: QuoteLineItemGridField.MINIMUM_ORDER_QUANTITY,
          minWidth: 150,
          sortComparator: gridNumberComparator,
          renderCell: cellFactory(QuantityCell, ({ row }) => ({ value: row.minimumOrderQuantity, unitId: row.unitId })),
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          field: QuoteLineItemGridField.TITLE,
          minWidth: 400,
          renderCell: cellFactory(EmptyFallbackCell),
        }),
        createColumn({
          field: QuoteLineItemGridField.STATUS,
          minWidth: 150,
          editable: !viewOnly,
          type: 'boolean',
          renderCell: cellFactory(SourcingCaseQuoteStatusCell, ({ api }) => ({
            viewOnly,
            onStatusToggle: (rowId: string | number, newStatus: TsrctSourcingCaseQuoteItemStatus) => {
              api.updateRows([
                {
                  id: rowId,
                  status: newStatus,
                },
              ]);
            },
          })),
        }),
      ],
    };
  }, [t, tColumn, viewOnly]);
};
